// 📁 src/hooks/useVoiceInput.js
// 🎤 Hook personalizado para Push-to-Talk con envío automático
// P0 Feature: Press & Hold to Record → Auto-Send

import { useState, useRef, useEffect, useCallback } from 'react';
import { speechToText } from '../services/voiceApiService';
import { audioRecorder } from '../utils/audioRecorder';

/**
 * 🎟️ TICKET-3: Máquina de estados explícita FSM (Finite State Machine)
 * Flujo: IDLE → READY → RECORDING → STOPPING → READY → IDLE
 * 
 * Estados:
 * - idle: Reposo completo (sin permisos o sin actividad)
 * - permission_pending: Solicitando permisos de micrófono
 * - ready: Permisos concedidos, listo para grabar
 * - recording: Grabación activa con análisis de señal en tiempo real
 * - stopping: Deteniendo grabación y procesando audio
 * - processing: Transcribiendo audio (STT)
 * - error: Error temporal (auto-recuperable)
 */
const VOICE_STATES = {
  IDLE: 'idle',
  PERMISSION_PENDING: 'permission_pending',
  READY: 'ready',
  RECORDING: 'recording',
  STOPPING: 'stopping',
  PROCESSING: 'processing',
  ERROR: 'error'
};

/**
 * 🎟️ TICKET-3: Transiciones válidas de la máquina de estados
 * Define qué cambios de estado son permitidos (previene errores fantasma)
 */
const VALID_TRANSITIONS = {
  [VOICE_STATES.IDLE]: [VOICE_STATES.PERMISSION_PENDING, VOICE_STATES.READY],
  [VOICE_STATES.PERMISSION_PENDING]: [VOICE_STATES.IDLE, VOICE_STATES.READY, VOICE_STATES.ERROR],
  [VOICE_STATES.READY]: [VOICE_STATES.RECORDING, VOICE_STATES.IDLE],
  [VOICE_STATES.RECORDING]: [VOICE_STATES.STOPPING],
  [VOICE_STATES.STOPPING]: [VOICE_STATES.PROCESSING, VOICE_STATES.READY, VOICE_STATES.ERROR],
  [VOICE_STATES.PROCESSING]: [VOICE_STATES.READY, VOICE_STATES.ERROR],
  [VOICE_STATES.ERROR]: [VOICE_STATES.IDLE, VOICE_STATES.READY]
};

/**
 * Hook de manejo de voz con Push-to-Talk
 * 
 * @param {Function} onSendMessage - Callback para enviar mensaje transcrito (text) => void
 * @param {Object} options - Configuración opcional
 * @param {number} options.minDuration - Duración mínima de audio en ms (default: 700)
 * @param {number} options.maxDuration - Duración máxima de audio en ms (default: 60000)
 * @param {number} options.sttTimeout - Timeout de transcripción en ms (default: 8000)
 * @param {Function} options.onAnalyticsEvent - Callback para telemetría (eventName, data) => void
 * 
 * @returns {Object} Estado y controles del sistema de voz
 */
export const useVoiceInput = (onSendMessage, options = {}) => {
  const {
    minDuration = 700,
    maxDuration = 60000,
    sttTimeout = 8000,
    onAnalyticsEvent = null
  } = options;

  // Estados
  const [voiceState, setVoiceState] = useState(VOICE_STATES.IDLE);
  const [recordingTime, setRecordingTime] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  
  // 🎟️ TICKET-3: Estado de análisis de señal de audio
  const [audioSignalStrength, setAudioSignalStrength] = useState(0); // 0-100%
  const [hasDetectedSpeech, setHasDetectedSpeech] = useState(false);

  // Referencias persistentes
  const recordingTimerRef = useRef(null);
  const maxDurationTimerRef = useRef(null);
  const sttTimeoutRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const isMountedRef = useRef(true);
  const audioAnalyserRef = useRef(null); // 🎟️ TICKET-3: AnalyserNode para RMS/peak
  const animationFrameRef = useRef(null);
  const isAnalyzingRef = useRef(false); // 🔧 FIX: Track análisis activo sin depender de voiceState
  
  /**
   * 🎟️ Analytics helper (DEBE estar ANTES de transitionTo para evitar TDZ)
   * Degradación silenciosa: si falla, no tumba la UI
   */
  const logEvent = useCallback((eventName, data = {}) => {
    try {
      console.log(`[VoiceInput][${eventName}]`, data);
      if (onAnalyticsEvent) {
        onAnalyticsEvent(eventName, data);
      }
    } catch (error) {
      // Degradación silenciosa: logging nunca debe romper el producto
      console.warn('[VoiceInput] Analytics error (non-blocking):', error);
    }
  }, [onAnalyticsEvent]);
  
  /**
   * 🎟️ TICKET-3: Transición de estado validada (previene transiciones inválidas)
   * NOTA: React garantiza que el updater function se ejecuta síncronamente,
   * pero no podemos capturar el return. Solución: siempre retornar true y
   * confiar en los logs para debugging.
   */
  const transitionTo = useCallback((newState) => {
    setVoiceState((currentState) => {
      const validNextStates = VALID_TRANSITIONS[currentState] || [];
      
      if (!validNextStates.includes(newState)) {
        console.error(`[VoiceInput][FSM] ❌ Transición inválida: ${currentState} → ${newState}`);
        console.error(`[VoiceInput][FSM] Transiciones válidas desde ${currentState}:`, validNextStates);
        logEvent('voice_invalid_transition', { from: currentState, to: newState });
        return currentState; // No cambiar estado
      }
      
      console.log(`[VoiceInput][FSM] ✅ ${currentState} → ${newState}`);
      return newState;
    });
    
    // NOTA: No podemos saber si la transición fue válida aquí (callback es "síncrono" pero el valor
    // no es capturado). En la práctica, las transiciones inválidas son bugs de programación, no
    // condiciones de runtime. Los logs son suficientes para debugging.
    return true;
  }, [logEvent]);

  // Limpiar timers al desmontar
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearAllTimers();
    };
  }, []);

  /**
   * Limpia todos los timers activos
   */
  const clearAllTimers = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
    if (sttTimeoutRef.current) {
      clearTimeout(sttTimeoutRef.current);
      sttTimeoutRef.current = null;
    }
  }, []);

  /**
   * 🎟️ TICKET-2/3: Solicita permisos de micrófono (PREFLIGHT - sin iniciar grabación)
   * Sistema de permisos 1-vez con localStorage + transición a READY
   */
  const requestPermission = useCallback(async () => {
    if (voiceState !== VOICE_STATES.IDLE) {
      return false;
    }

    logEvent('voice_permission_request');
    transitionTo(VOICE_STATES.PERMISSION_PENDING);
    setErrorMessage(null);

    try {
      await audioRecorder.requestPermissions();
      
      // ✅ Permiso concedido - guardar en localStorage (1 sola vez)
      localStorage.setItem('voice_permission_granted', 'true');
      logEvent('voice_permission_granted');
      
      // 🎟️ TICKET-3: Transición a READY (listo para grabar)
      transitionTo(VOICE_STATES.READY);
      
      return true;
    } catch (error) {
      console.error('[VoiceInput] Permiso denegado:', error);
      logEvent('voice_permission_denied', { message: error.message });
      
      // Marcar como rechazado (para no volver a pedir)
      localStorage.setItem('voice_permission_denied', 'true');
      
      transitionTo(VOICE_STATES.ERROR);
      setErrorMessage('Micrófono bloqueado. Verifica configuración del navegador.');
      
      // 🎟️ TICKET-3: Auto-recuperación rápida (3s)
      setTimeout(() => {
        if (isMountedRef.current) {
          transitionTo(VOICE_STATES.IDLE);
          setErrorMessage(null);
        }
      }, 3000);
      
      return false;
    }
  }, [voiceState, logEvent, transitionTo]);

  /**
   * 🎟️ TICKET-2/3: Inicia la grabación con PREFLIGHT check automático
   * Si no hay permisos, los solicita (1 sola vez) y NO inicia grabación
   * Permite iniciar desde IDLE (sin permisos) o READY (con permisos ya concedidos)
   */
  const startRecording = useCallback(async () => {
    // 🎟️ TICKET-3: Permitir grabar desde IDLE o READY
    const canStartRecording = voiceState === VOICE_STATES.IDLE || voiceState === VOICE_STATES.READY;
    
    if (!canStartRecording) {
      console.warn('[VoiceInput] Intento de iniciar grabación desde estado inválido:', voiceState);
      return;
    }

    logEvent('voice_record_start_attempt');
    setRecordingTime(0);
    setErrorMessage(null);

    try {
      // 🎟️ TICKET-2/3: PREFLIGHT CHECK - Verificar permisos ANTES de grabar
      const hasPermission = await audioRecorder.checkPermissions();
      
      if (!hasPermission) {
        console.log('[VoiceInput] Permisos no concedidos - iniciando preflight');
        
        // Verificar si ya rechazó anteriormente
        const wasDenied = localStorage.getItem('voice_permission_denied') === 'true';
        if (wasDenied) {
          logEvent('voice_permission_blocked');
          transitionTo(VOICE_STATES.ERROR);
          setErrorMessage('Micrófono bloqueado. Verifica configuración del navegador.');
          setTimeout(() => {
            if (isMountedRef.current) {
              transitionTo(VOICE_STATES.IDLE);
              setErrorMessage(null);
            }
          }, 3000);
          return;
        }
        
        // Primera vez - solicitar permisos (PREFLIGHT)
        const granted = await requestPermission();
        if (!granted) {
          // Usuario rechazó - NO continuar
          return;
        }
        
        // Permiso concedido pero NO iniciar grabación automáticamente
        // Usuario debe presionar de nuevo (UX clara)
        console.log('[VoiceInput] Permiso concedido - presiona de nuevo para grabar');
        return;
      }
      
      // 🎟️ TICKET-3: Asegurar que estamos en READY antes de grabar
      if (voiceState === VOICE_STATES.IDLE) {
        // Primera vez con permisos - transición a READY
        console.log('[VoiceInput] Transicionando de IDLE → READY');
        transitionTo(VOICE_STATES.READY);
      }
      
      // En este punto, estamos en READY (o ya lo estábamos)
      // NO verificar voiceState aquí porque setVoiceState es asíncrono
      
      // 🎟️ TICKET-3: Iniciar grabación con análisis de señal
      recordingStartTimeRef.current = Date.now();
      logEvent('voice_record_start');
      setHasDetectedSpeech(false);
      setAudioSignalStrength(0);
      
      console.log('[VoiceInput] Transicionando READY → RECORDING');
      transitionTo(VOICE_STATES.RECORDING);
      
      // Iniciar análisis de señal en tiempo real
      const { stream, analyser } = await audioRecorder.startWithAnalyzer();
      audioAnalyserRef.current = analyser;
      isAnalyzingRef.current = true; // 🔧 Marcar análisis activo
      
      // 🎟️ TICKET-3: Monitoreo de señal RMS/peak en tiempo real
      const analyzeSignal = () => {
        // 🔧 FIX: Usar ref en vez de voiceState (evita stale closure)
        if (!isAnalyzingRef.current || !audioAnalyserRef.current) {
          return;
        }
        
        const dataArray = new Uint8Array(audioAnalyserRef.current.frequencyBinCount);
        audioAnalyserRef.current.getByteTimeDomainData(dataArray);
        
        // Calcular RMS (Root Mean Square) - medida de volumen
        let sum = 0;
        let peak = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const normalized = (dataArray[i] - 128) / 128; // -1 a 1
          sum += normalized * normalized;
          peak = Math.max(peak, Math.abs(normalized));
        }
        const rms = Math.sqrt(sum / dataArray.length);
        
        // Convertir a porcentaje (0-100%)
        const signalPercent = Math.min(100, Math.floor(rms * 100 * 5)); // x5 para amplificar
        setAudioSignalStrength(signalPercent);
        
        // Detectar habla (umbral: RMS > 5% O peak > 10%)
        if (rms > 0.05 || peak > 0.1) {
          setHasDetectedSpeech(true);
        }
        
        animationFrameRef.current = requestAnimationFrame(analyzeSignal);
      };
      
      analyzeSignal();

      // Timer de UI (actualiza contador cada 100ms para feedback visual)
      recordingTimerRef.current = setInterval(() => {
        if (!isMountedRef.current) return;
        const elapsed = Date.now() - recordingStartTimeRef.current;
        setRecordingTime(Math.floor(elapsed / 1000));
      }, 100);

      // Timer de máxima duración
      maxDurationTimerRef.current = setTimeout(() => {
        console.warn('[VoiceInput] Máxima duración alcanzada, deteniendo automáticamente');
        stopRecording();
      }, maxDuration);

    } catch (error) {
      console.error('[VoiceInput] Error al iniciar grabación:', error);
      logEvent('voice_record_error', { type: 'start_failed', message: error.message });
      
      clearAllTimers();
      setVoiceState(VOICE_STATES.ERROR);
      setErrorMessage('Error al iniciar grabación. Intenta nuevamente.');
      
      // Auto-reset a idle después de 3s (fast recovery)
      setTimeout(() => {
        if (isMountedRef.current) {
          setVoiceState(VOICE_STATES.IDLE);
          setErrorMessage(null);
        }
      }, 3000);
    }
  }, [voiceState, logEvent, maxDuration, clearAllTimers, requestPermission]);

  /**
   * 🎟️ TICKET-3: Detiene la grabación y procesa el audio con validación de señal
   */
  const stopRecording = useCallback(async () => {
    if (voiceState !== VOICE_STATES.RECORDING) {
      console.warn('[VoiceInput] Intento de detener grabación en estado no-recording:', voiceState);
      return;
    }

    const recordingDuration = Date.now() - recordingStartTimeRef.current;
    logEvent('voice_record_stop', { 
      duration_ms: recordingDuration,
      detected_speech: hasDetectedSpeech,
      signal_strength: audioSignalStrength
    });

    // 🎟️ TICKET-3: Detener análisis de señal
    isAnalyzingRef.current = false; // 🔧 FIX: Marcar análisis inactivo
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    audioAnalyserRef.current = null;

    clearAllTimers();
    transitionTo(VOICE_STATES.STOPPING);
    
    // 🎟️ TICKET-3: Validación de señal ANTES de transcribir
    if (!hasDetectedSpeech && recordingDuration > 1000) {
      console.warn('[VoiceInput] No se detectó señal de voz durante la grabación');
      logEvent('voice_no_signal_detected');
      
      audioRecorder.cancel();
      
      transitionTo(VOICE_STATES.ERROR);
      setErrorMessage('No se detectó voz. Habla más cerca del micrófono.');
      
      // 🎟️ TICKET-3: Auto-recuperación rápida (2s, no bloqueante)
      setTimeout(() => {
        if (isMountedRef.current) {
          transitionTo(VOICE_STATES.READY);
          setErrorMessage(null);
        }
      }, 2000);
      return;
    }
    
    transitionTo(VOICE_STATES.PROCESSING);

    try {
      // Obtener audio grabado
      const { blob, mimeType, durationMs } = await audioRecorder.stop();

      console.log('[VoiceInput] Audio grabado:', {
        size: `${(blob.size / 1024).toFixed(2)} KB`,
        duration: `${(durationMs / 1000).toFixed(1)}s`,
        type: mimeType
      });

      // Validar duración mínima
      if (durationMs < minDuration) {
        throw new Error('Grabación muy corta. Mantén presionado un poco más.');
      }

      // Transcribir con timeout
      logEvent('voice_stt_start');
      const startTime = Date.now();

      // Promise de transcripción
      const transcriptionPromise = (async () => {
        const formData = new FormData();
        formData.append('file', blob, 'audio.webm');
        formData.append('language', 'es');
        return await speechToText(formData);
      })();

      // Promise de timeout
      const timeoutPromise = new Promise((_, reject) => {
        sttTimeoutRef.current = setTimeout(() => {
          reject(new Error('No se pudo transcribir. Reintenta.'));
        }, sttTimeout);
      });

      // Race: transcripción vs timeout
      const { text } = await Promise.race([transcriptionPromise, timeoutPromise]);
      clearTimeout(sttTimeoutRef.current);

      const sttDuration = Date.now() - startTime;
      logEvent('voice_stt_success', { duration_ms: sttDuration, text_length: text?.length || 0 });

      console.log('[VoiceInput] Texto transcrito:', text);

      // ========== VALIDACIÓN ROBUSTA DE TEXTO ==========
      // Validar texto no vacío
      if (!text || text.trim() === '') {
        throw new Error('No se detectó voz. Intenta de nuevo.');
      }

      const trimmedText = text.trim();
      const wordCount = trimmedText.split(/\s+/).length;
      const avgWordLength = trimmedText.replace(/\s+/g, '').length / wordCount;

      // Filtrar transcripciones basura (ruido interpretado como texto)
      // Características de texto basura:
      // - Muy corto (< 3 caracteres) y sin sentido
      // - Una sola palabra muy corta (< 2 letras)
      // - Patrones repetitivos (ej: "a a a", "mmm mmm")
      // - Caracteres sueltos o puntuación sin contexto
      const isGarbage = (
        trimmedText.length < 3 ||                                    // Muy corto
        (wordCount === 1 && trimmedText.length < 3) ||              // Una palabra muy corta
        /^[^a-záéíóúñA-ZÁÉÍÓÚÑ]+$/.test(trimmedText) ||            // Solo puntuación/números
        /^(.)\1+$/.test(trimmedText) ||                             // Caracteres repetidos (aaa)
        avgWordLength < 2                                            // Palabras promedio muy cortas
      );

      if (isGarbage) {
        console.warn('[VoiceInput] Texto descartado por ser basura:', text);
        throw new Error('No se detectó voz clara. Habla más cerca del micrófono.');
      }
      // ========== FIN VALIDACIÓN ==========

      // ========== AUTO-SEND ==========
      // Enviar mensaje automáticamente tras transcripción exitosa
      if (onSendMessage) {
        logEvent('voice_send_success', { text_length: trimmedText.length });
        onSendMessage(trimmedText);
      }

      // 🎟️ TICKET-3: Volver a READY (listo para siguiente grabación)
      if (isMountedRef.current) {
        transitionTo(VOICE_STATES.READY);
        setRecordingTime(0);
        setAudioSignalStrength(0);
        setHasDetectedSpeech(false);
      }

    } catch (error) {
      console.error('[VoiceInput] Error en procesamiento:', error);
      
      const errorType = error.message.includes('timeout') ? 'stt_timeout' :
                        error.message.includes('corto') ? 'audio_too_short' :
                        error.message.includes('voz') ? 'no_speech' : 'stt_error';
      
      logEvent('voice_stt_error', { type: errorType, message: error.message });

      if (isMountedRef.current) {
        transitionTo(VOICE_STATES.ERROR);
        setErrorMessage(error.message || 'Error al procesar audio');
        setRecordingTime(0);

        // 🎟️ TICKET-3: Auto-recuperación más rápida (3s, no bloqueante)
        setTimeout(() => {
          if (isMountedRef.current) {
            transitionTo(VOICE_STATES.READY);
            setErrorMessage(null);
          }
        }, 3000);
      }
    }
  }, [voiceState, logEvent, minDuration, sttTimeout, clearAllTimers, onSendMessage, transitionTo, hasDetectedSpeech, audioSignalStrength]);

  /**
   * 🎟️ TICKET-3: Cancela la grabación actual sin procesar (con limpieza completa)
   */
  const cancelRecording = useCallback(() => {
    if (voiceState !== VOICE_STATES.RECORDING) return;

    logEvent('voice_record_cancel');
    
    // 🎟️ TICKET-3: Detener análisis de señal
    isAnalyzingRef.current = false; // 🔧 FIX: Marcar análisis inactivo
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    audioAnalyserRef.current = null;
    
    audioRecorder.cancel();
    clearAllTimers();
    
    if (isMountedRef.current) {
      transitionTo(VOICE_STATES.READY);
      setRecordingTime(0);
      setErrorMessage(null);
      setAudioSignalStrength(0);
      setHasDetectedSpeech(false);
    }
  }, [voiceState, logEvent, clearAllTimers, transitionTo]);

  /**
   * Maneja el evento Escape para cancelar grabación
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && voiceState === VOICE_STATES.RECORDING) {
        e.preventDefault();
        cancelRecording();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [voiceState, cancelRecording]);

  /**
   * Maneja cambio de visibilidad de la pestaña
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && voiceState === VOICE_STATES.RECORDING) {
        console.warn('[VoiceInput] Tab cambió a hidden durante grabación, cancelando');
        cancelRecording();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [voiceState, cancelRecording]);

  /**
   * Formatea el tiempo de grabación en formato M:SS
   */
  const formatRecordingTime = useCallback(() => {
    const mins = Math.floor(recordingTime / 60);
    const secs = recordingTime % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, [recordingTime]);

  return {
    // 🎟️ TICKET-3: Estado FSM
    voiceState,
    isIdle: voiceState === VOICE_STATES.IDLE,
    isPermissionPending: voiceState === VOICE_STATES.PERMISSION_PENDING,
    isReady: voiceState === VOICE_STATES.READY,
    isRecording: voiceState === VOICE_STATES.RECORDING,
    isStopping: voiceState === VOICE_STATES.STOPPING,
    isProcessing: voiceState === VOICE_STATES.PROCESSING,
    isError: voiceState === VOICE_STATES.ERROR,
    
    // Datos
    recordingTime,
    formattedTime: formatRecordingTime(),
    errorMessage,
    
    // 🎟️ TICKET-3: Análisis de señal
    audioSignalStrength,
    hasDetectedSpeech,
    
    // Controles
    requestPermission,
    startRecording,
    stopRecording,
    cancelRecording
  };
};

export default useVoiceInput;
